import { useCallback, useState } from "react";
import { Smartphone, Download, Scan, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePwaInstallPrompt } from "@/hooks/use-pwa-install-prompt";
import { useRouteModal } from "@/hooks/use-route-modal";
import IphoneInstructionsDialog from "./pwa/IphoneInstructionsDialog";

const MobilePWA = () => {
  const { active, onIntall, isInstallPromptSupported } = usePwaInstallPrompt();

  const [isInstalled, setIsInstalled] = useState(false);

  const [isIphoneInstructionsOpen, setIsIphoneInstructionsOpen] = useRouteModal(
    "iphone-instructions",
  );

  const onClickInstall = useCallback(async () => {
    if (!isInstallPromptSupported) {
      setIsIphoneInstructionsOpen(true);
      return;
    }

    const result = await onIntall();

    if (result) {
      setIsInstalled(true);
    }
  }, [onIntall, isInstallPromptSupported, setIsIphoneInstructionsOpen]);

  if ((!active || isInstalled) && isInstallPromptSupported) {
    return null;
  }

  return (
    <section className="py-20 bg-gradient-to-br from-llama-white to-holo-blue/5 dark:bg-gradient-to-br dark:from-deep-indigo dark:via-slate-800 dark:to-deep-indigo/80 relative overflow-hidden border-0">
      <div className="absolute inset-0 bg-gradient-to-tl dark:bg-gradient-to-tr from-llama-white via-holo-blue/10 to-llama-white dark:from-background/60 dark:via-transparent dark:to-background/80 pointer-events-none"></div>
      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className=" ">
            <h2 className="text-center lg:text-left text-4xl md:text-5xl font-semibold font-poppins mb-6 text-deep-indigo dark:text-llama-white/80">
              Add to Home Screen for 1-tap access
            </h2>
            <p className="text-center lg:text-left text-lg text-deep-indigo/70 dark:text-llama-white/80 font-inter mb-8 leading-relaxed max-w-lg">
              Get the full ThePokeLlama experience on your mobile device. Scan
              cards, track prices, and manage your collection on the go.
            </p>

            <div className="space-y-6 mb-8 mx-auto max-w-xs lg:max-w-none">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-electric-yellow to-holo-blue rounded-xl flex items-center justify-center">
                  <Download className="w-6 h-6 text-deep-indigo" />
                </div>
                <div>
                  <h3 className="font-semibold font-poppins text-deep-indigo dark:text-llama-white">
                    One-tap installation
                  </h3>
                  <p className="text-deep-indigo/70 dark:text-llama-white/70 font-inter text-sm">
                    Add to home screen like a native app
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-holo-blue to-poke-red rounded-xl flex items-center justify-center">
                  <Scan className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold font-poppins text-deep-indigo dark:text-llama-white">
                    Card scanning
                  </h3>
                  <p className="text-deep-indigo/70 dark:text-llama-white/70 font-inter text-sm">
                    Snap photos to add cards instantly
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-poke-red to-electric-yellow rounded-xl flex items-center justify-center">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold font-poppins text-deep-indigo dark:text-llama-white">
                    Offline access
                  </h3>
                  <p className="text-deep-indigo/70 dark:text-llama-white/70 font-inter text-sm">
                    View your collection anywhere
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center lg:text-left">
              <Button
                onClick={onClickInstall}
                className="bg-gradient-to-r from-deep-indigo to-holo-blue hover:from-deep-indigo/90 hover:to-holo-blue/90 text-white font-bold font-inter px-8 py-3 rounded-full"
              >
                <Smartphone className="w-5 h-5 mr-2" />
                Try Mobile App
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="backdrop-blur-sm border-0 shadow-2xl max-w-sm mx-auto">
              <div className="bg-deep-indigo dark:bg-slate-800 rounded-3xl p-4 shadow-xl">
                <div className="bg-llama-white dark:bg-slate-700 rounded-2xl p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-holo-blue to-deep-indigo rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">🦙</span>
                      </div>
                      <span className="font-bold font-poppins text-deep-indigo dark:text-llama-white text-sm">
                        ThePokeLlama
                      </span>
                    </div>
                    <Scan className="w-6 h-6 text-holo-blue" />
                  </div>

                  <div className="bg-gradient-to-br from-electric-yellow/20 to-holo-blue/20 dark:from-electric-yellow/30 dark:to-holo-blue/30 rounded-xl p-4">
                    <div className="w-full h-24 bg-gradient-to-br from-holo-blue to-deep-indigo rounded-lg mb-2"></div>
                    <div className="space-y-1">
                      <div className="h-3 bg-deep-indigo/20 dark:bg-llama-white/20 rounded w-3/4"></div>
                      <div className="h-2 bg-deep-indigo/10 dark:bg-llama-white/10 rounded w-1/2"></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-electric-yellow/10 dark:bg-electric-yellow/20 rounded-lg p-2 text-center">
                      <div className="text-xs font-bold text-deep-indigo dark:text-llama-white">
                        247
                      </div>
                      <div className="text-xs text-deep-indigo/60 dark:text-llama-white/60">
                        Cards
                      </div>
                    </div>
                    <div className="bg-holo-blue/10 dark:bg-holo-blue/20 rounded-lg p-2 text-center">
                      <div className="text-xs font-bold text-deep-indigo dark:text-llama-white">
                        $2.8K
                      </div>
                      <div className="text-xs text-deep-indigo/60 dark:text-llama-white/60">
                        Value
                      </div>
                    </div>
                    <div className="bg-poke-red/10 dark:bg-poke-red/20 rounded-lg p-2 text-center">
                      <div className="text-xs font-bold text-deep-indigo dark:text-llama-white">
                        1,247
                      </div>
                      <div className="text-xs text-deep-indigo/60 dark:text-llama-white/60">
                        Score
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -top-4 -right-2 md:right-8 md:-top-8 w-8 h-8 bg-electric-yellow rounded-full animate-bounce"></div>
            <div className="absolute -bottom-4 -left-2 md:left-8 md:-bottom-8 w-6 h-6 bg-holo-blue rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>

      <IphoneInstructionsDialog
        open={isIphoneInstructionsOpen}
        onOpenChange={setIsIphoneInstructionsOpen}
      />
    </section>
  );
};

export default MobilePWA;
